import { useState, useEffect, useRef } from 'react';
import { Bell, Check, AlertTriangle, Info, Star, X, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../lib/supabase';

interface Notification {
  id: string;
  user_id: string;
  title: string;
  body: string;
  type: string;
  is_read: boolean;
  created_at?: string;
}

// Animation variants for the notification items
const listVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.95 },
  show: { 
    opacity: 1, 
    y: 0, 
    scale: 1,
    transition: { 
      type: "spring", 
      stiffness: 260, 
      damping: 20,
      mass: 0.5,
    } 
  },
  exit: { 
    opacity: 0, 
    y: -10, 
    transition: { 
      duration: 0.2, 
      ease: "easeOut" 
    } 
  }
};

export default function NotificationIcon() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch notifications from Supabase
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) return;
      
      // Fetch notifications for the current user or global notifications (null user_id)
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .or(`user_id.eq.${user.id},user_id.is.null`)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      setNotifications(data || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Mark notification as read
  const markAsRead = async (id: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', id);
      
      if (error) throw error;
      
      // Update local state
      setNotifications(prevNotifications => 
        prevNotifications.map(notification => 
          notification.id === id 
            ? { ...notification, is_read: true } 
            : notification
        )
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) return;
      
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .or(`user_id.eq.${user.id},user_id.is.null`)
        .eq('is_read', false);
      
      if (error) throw error;
      
      // Update local state
      setNotifications(prevNotifications => 
        prevNotifications.map(notification => ({ ...notification, is_read: true }))
      );
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // Fetch notifications on component mount and set up real-time subscription
  useEffect(() => {
    fetchNotifications();
    
    // Set up real-time subscription for new notifications
    const subscription = supabase
      .channel('notifications_changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'notifications' 
        }, 
        (_payload) => {
          fetchNotifications();
        }
      )
      .subscribe();
    
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Count unread notifications
  const unreadCount = notifications.filter(notification => !notification.is_read).length;

  // Function to get notification type color and icon
  const getNotificationTypeStyles = (type: string) => {
    switch(type?.toLowerCase()) {
      case 'success':
        return { 
          bgColor: 'bg-gradient-to-r from-[#062926] to-[#0a433e]', 
          borderColor: 'border-[#0fcfb8]',
          glowColor: 'shadow-[#0fcfb8]/20',
          iconColor: 'text-[#0fcfb8]', 
          icon: <Check className="w-4 h-4" />
        };
      case 'warning':
        return { 
          bgColor: 'bg-gradient-to-r from-[#332210] to-[#463016]', 
          borderColor: 'border-[#ffb74d]',
          glowColor: 'shadow-[#ffb74d]/20', 
          iconColor: 'text-[#ffb74d]', 
          icon: <AlertTriangle className="w-4 h-4" />
        };
      case 'error':
        return { 
          bgColor: 'bg-gradient-to-r from-[#341215] to-[#481a1d]', 
          borderColor: 'border-[#ff6b6b]',
          glowColor: 'shadow-[#ff6b6b]/20', 
          iconColor: 'text-[#ff6b6b]', 
          icon: <X className="w-4 h-4" />
        };
      case 'highlight':
        return { 
          bgColor: 'bg-gradient-to-r from-[#1a1033] to-[#2d1b55]', 
          borderColor: 'border-[#a78bfa]',
          glowColor: 'shadow-[#a78bfa]/20', 
          iconColor: 'text-[#a78bfa]', 
          icon: <Star className="w-4 h-4" />
        };
      case 'info':
      default:
        return { 
          bgColor: 'bg-gradient-to-r from-[#0a192f] to-[#172a46]', 
          borderColor: 'border-[#c9fffc]',
          glowColor: 'shadow-[#c9fffc]/20', 
          iconColor: 'text-[#c9fffc]', 
          icon: <Info className="w-4 h-4" />
        };
    }
  };

  // Get time ago format
  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
    }
    if (diffHours > 0) {
      return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
    }
    if (diffMins > 0) {
      return diffMins === 1 ? '1 minute ago' : `${diffMins} minutes ago`;
    }
    return 'Just now';
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <motion.button 
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-[#c9fffc]/80 hover:text-[#c9fffc] focus:outline-none transition-colors"
        whileTap={{ scale: 0.92 }}
        whileHover={{ scale: 1.05 }}
      >
        <motion.div
          animate={isOpen ? { rotate: [0, -15, 15, -10, 10, 0] } : {}}  
          transition={{ duration: 0.5, ease: "easeInOut" }}
        >
          <Bell className="h-6 w-6" />
        </motion.div>
        
        {unreadCount > 0 && (
          <motion.span 
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute top-0 right-0 h-5 w-5 bg-[#c9fffc] rounded-full text-[10px] flex items-center justify-center text-[#0f172a] font-bold shadow-md shadow-[#c9fffc]/30"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </motion.span>
        )}
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 350, damping: 25 }}
            className="absolute right-0 mt-2 w-80 bg-[#141C28]/90 backdrop-blur-lg backdrop-saturate-150 rounded-lg shadow-xl border border-white/10 overflow-hidden z-50"
          >
            <div className="px-4 py-3 border-b border-white/10 flex justify-between items-center">
              <h3 className="text-white text-sm font-medium flex items-center">
                Notifications
                {unreadCount > 0 && (
                  <motion.span 
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.1 }}
                    className="ml-2 text-xs bg-[#c9fffc]/10 text-[#c9fffc] rounded-full px-2 py-0.5"
                  >
                    {unreadCount} new
                  </motion.span>
                )}
              </h3>
              {unreadCount > 0 && (
                <motion.button 
                  onClick={markAllAsRead}
                  whileHover={{ scale: 1.05, backgroundColor: 'rgba(201, 255, 252, 0.15)' }}
                  whileTap={{ scale: 0.95 }}
                  className="text-xs text-[#c9fffc] bg-[#c9fffc]/5 px-3 py-1 rounded-full transition-colors"
                >
                  Mark all as read
                </motion.button>
              )}
            </div>
            
            <div className="max-h-[70vh] overflow-y-auto thin-scrollbar">
              {loading ? (
                <div className="p-8 text-center text-gray-400 flex flex-col items-center justify-center">
                  <div className="relative w-10 h-10">
                    <motion.div 
                      className="absolute inset-0 rounded-full border-2 border-[#c9fffc]/20"
                      style={{ borderTopColor: '#c9fffc' }}
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                    />
                  </div>
                  <motion.p 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="mt-3 text-[#c9fffc]/70"
                  >
                    Loading notifications...
                  </motion.p>
                </div>
              ) : notifications.length === 0 ? (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="p-12 text-center flex flex-col items-center justify-center"
                >
                  <motion.div 
                    initial={{ scale: 0.8, opacity: 0.5 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                    className="w-16 h-16 rounded-full bg-[#1a2533] flex items-center justify-center mb-4"
                  >
                    <Bell className="w-8 h-8 text-[#c9fffc]/30" />
                  </motion.div>
                  <p className="text-[#c9fffc]/80 font-medium">No notifications yet</p>
                  <p className="text-xs text-[#c9fffc]/40 mt-1">We'll notify you when something happens</p>
                </motion.div>
              ) : (
                <motion.div
                  variants={listVariants}
                  initial="hidden"
                  animate="show"
                >
                  {notifications.map(notification => {
                    const typeStyles = getNotificationTypeStyles(notification.type);
                    return (
                      <motion.div 
                        key={notification.id}
                        variants={itemVariants}
                        exit="exit"
                        layoutId={`notification-${notification.id}`}
                        className={`p-4 border-b border-white/5 transition-all duration-300 cursor-pointer relative overflow-hidden`}
                        onClick={() => markAsRead(notification.id)}
                        whileHover={{ backgroundColor: 'rgba(26, 37, 51, 0.6)' }}
                      >
                        {/* Unread indicator with glow effect */}
                        {!notification.is_read && (
                          <motion.div 
                            className="absolute right-4 top-4 h-2 w-2 rounded-full bg-[#c9fffc] shadow-sm shadow-[#c9fffc]/50"
                            animate={{ 
                              scale: [1, 1.2, 1],
                              opacity: [1, 0.7, 1]
                            }}
                            transition={{ 
                              duration: 2, 
                              repeat: Infinity,
                              ease: "easeInOut" 
                            }}
                          />
                        )}
                        
                        <div className="flex space-x-3">
                          {/* Icon container with glow effect */}
                          <motion.div 
                            className={`flex-shrink-0 h-10 w-10 rounded-lg ${typeStyles.bgColor} ${typeStyles.borderColor} border flex items-center justify-center ${typeStyles.glowColor} shadow-lg`}
                            whileHover={{ scale: 1.08, boxShadow: '0 0 12px 2px rgba(201, 255, 252, 0.3)' }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <motion.div className={typeStyles.iconColor}>
                              {typeStyles.icon}
                            </motion.div>
                          </motion.div>
                          
                          <div className="flex-1">
                            <motion.h4 
                              className={`text-sm font-medium ${!notification.is_read ? 'text-white' : 'text-[#c9fffc]/80'}`}
                              whileHover={{ color: '#c9fffc' }}
                            >
                              {notification.title}
                            </motion.h4>
                            <motion.p 
                              className="text-xs text-[#c9fffc]/60 mt-1 line-clamp-2"
                            >
                              {notification.body}
                            </motion.p>
                            {notification.created_at && (
                              <p className="text-xs text-[#c9fffc]/40 mt-1.5 flex items-center gap-1">
                                <span className="inline-block h-1 w-1 rounded-full bg-[#c9fffc]/30"></span>
                                {getTimeAgo(notification.created_at)}
                              </p>
                            )}
                          </div>
                          
                          <motion.div 
                            className="flex items-center text-[#c9fffc]/30"
                            whileHover={{ x: 3, color: 'rgba(201, 255, 252, 0.6)' }}
                          >
                            <ChevronRight className="w-4 h-4" />
                          </motion.div>
                        </div>
                      </motion.div>
                    );
                  })}
                </motion.div>
              )}
            </div>
            
            {/* Custom scrollbar styles */}
            <style dangerouslySetInnerHTML={{ __html: `
              .thin-scrollbar {
                scrollbar-width: thin;
                scrollbar-color: rgba(201, 255, 252, 0.3) transparent;
              }
              .thin-scrollbar::-webkit-scrollbar {
                width: 4px;
              }
              .thin-scrollbar::-webkit-scrollbar-track {
                background: transparent;
              }
              .thin-scrollbar::-webkit-scrollbar-thumb {
                background-color: rgba(201, 255, 252, 0.2);
                border-radius: 20px;
              }
              .thin-scrollbar::-webkit-scrollbar-thumb:hover {
                background-color: rgba(201, 255, 252, 0.4);
              }
            `}} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
